import React, { useMemo, useState, useEffect } from 'react';
import styles from './O_CreationCapsuleActivation.module.css';
import A_RadioItem from '@/components/A_Atoms/A_RadioItem/A_RadioItem';
import A_Button from '@/components/A_Atoms/A_Button/A_Button';
import M_InviteItem from '@/components/M_Molecules/M_InviteItem/M_InviteItem';
import S_ActivationCapsulePopup from '@/components/S_Super-Organisms/S_ActivationCapsulePopup/S_ActivationCapsulePopup';
import S_InviteCoauthorsPopup from '@/components/S_Super-Organisms/S_InviteCoauthorsPopup/S_InviteCoauthorsPopup';
import { useLanguage } from '@/contexts/LanguageContext';
import { getTranslation } from '@/constants/translations';
import { formatActivationDate, parseScheduledDate } from '@/utils/date';
import { User } from '@/types/user';
import { capsulesApi, CapsuleInvite } from '@/services/capsulesApi';
import { usersApi } from '@/services/usersApi';
import { useApi } from '@/hooks/useApi';
import cn from 'classnames';

export type ActivationType = 'manual' | 'scheduled' | 'custodian';

interface O_CreationCapsuleActivationProps {
  activationType?: ActivationType;
  onActivationTypeChange?: (type: ActivationType) => void;
  scheduledDate?: string;
  onDateChange?: (date: string) => void;
  selectedCustodians?: string[];
  onCustodianSelect?: (user: User) => void;
  onCustodianEmailInvite?: (email: string) => void;
  onCustodianRemove?: (userId: string) => void;
  capsuleId?: string | number;
}

export default function O_CreationCapsuleActivation({
  activationType = 'manual',
  onActivationTypeChange,
  scheduledDate,
  onDateChange,
  selectedCustodians = [],
  onCustodianSelect,
  onCustodianEmailInvite,
  onCustodianRemove,
  capsuleId,
}: O_CreationCapsuleActivationProps) {
  const { language } = useLanguage();
  const t = (key: string) => getTranslation(language, key);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isInviteCustodianPopupOpen, setIsInviteCustodianPopupOpen] =
    useState(false);

  const {
    data: invites,
    loading: invitesLoading,
    refetch: refetchInvites,
  } = useApi(
    () =>
      capsuleId && activationType === 'custodian'
        ? capsulesApi.getCapsuleInvites(capsuleId)
        : Promise.resolve({ success: true, data: [] }),
    [capsuleId, activationType]
  );

  const { data: custodiansInfo } = useApi(async () => {
    if (capsuleId || selectedCustodians.length === 0) return [];
    const results = await Promise.all(
      selectedCustodians.map(async userId => {
        try {
          const res = await usersApi.getUserInfo(userId);
          if (res.success && res.data) {
            const firstName = res.data.personal?.firstName || '';
            const lastName = res.data.personal?.lastName || '';
            const fullName = `${firstName} ${lastName}`.trim();
            return {
              id: userId,
              name: fullName || res.data.handle || `User ${userId}`,
              handle: res.data.handle || '',
              firstName,
              lastName,
              avatar: res.data.avatar || undefined,
            };
          }
          return {
            id: userId,
            name: `User ${userId}`,
            handle: '',
            firstName: '',
            lastName: '',
            avatar: undefined,
          };
        } catch {
          return {
            id: userId,
            name: `User ${userId}`,
            handle: '',
            firstName: '',
            lastName: '',
            avatar: undefined,
          };
        }
      })
    );
    return results;
  }, [selectedCustodians.join(','), capsuleId]);

  useEffect(() => {
    if (isInviteCustodianPopupOpen === false && capsuleId) {
      refetchInvites();
    }
  }, [isInviteCustodianPopupOpen, capsuleId, refetchInvites]);

  const uniqueInvites = React.useMemo(() => {
    if (!invites?.data) return [];
    const seen = new Map<string, CapsuleInvite>();
    const sortedInvites = [...invites.data].sort((a, b) => {
      const aId = parseInt(String(a.id), 10);
      const bId = parseInt(String(b.id), 10);
      return bId - aId;
    });
    for (const invite of sortedInvites) {
      const userId = String(invite.to_user.id);
      if (!seen.has(userId)) seen.set(userId, invite);
    }
    return Array.from(seen.values());
  }, [invites?.data]);

  const coauthorInvites = uniqueInvites.filter(inv => !inv.accepted);
  const acceptedCustodians = uniqueInvites.filter(inv => inv.accepted);

  const hasCustodians =
    capsuleId && activationType === 'custodian'
      ? coauthorInvites.length > 0 || acceptedCustodians.length > 0
      : activationType === 'custodian' && selectedCustodians.length > 0;

  const handleRemove = async (userId: string | number) => {
    if (capsuleId) {
      try {
        await capsulesApi.removeCoauthor(capsuleId, userId);
        refetchInvites();
      } catch {
        // ошибка удаления
      }
    } else {
      onCustodianRemove?.(String(userId));
    }
  };

  const handleReinvite = async (userId: string | number) => {
    if (!capsuleId) return;
    try {
      await capsulesApi.reinviteCoauthor(capsuleId, userId);
      refetchInvites();
    } catch {
      // ошибка повторной отправки
    }
  };

  const createModeCustodiansList = useMemo(() => {
    if (custodiansInfo && custodiansInfo.length > 0) return custodiansInfo;
    return selectedCustodians.map(id => ({
      id,
      name: `User ${id}`,
      handle: '',
      firstName: '',
      lastName: '',
      avatar: undefined as string | undefined,
    }));
  }, [custodiansInfo, selectedCustodians]);

  const inviteFromUserInfo = (userInfo: {
    id: string;
    name: string;
    handle?: string;
    firstName?: string;
    lastName?: string;
    avatar?: string;
  }): CapsuleInvite => ({
    id: userInfo.id,
    capsule: '',
    from_user: { id: '', username: '' },
    to_user: {
      id: userInfo.id,
      username: userInfo.handle || userInfo.name,
      first_name: userInfo.firstName || '',
      last_name: userInfo.lastName || '',
      avatar: userInfo.avatar || null,
    },
    created_at: '',
    accepted: false,
    is_read: false,
  });

  const renderCustodiansBlockContent = () => {
    if (capsuleId && invitesLoading) {
      return (
        <span className={cn(styles.custodians_text, 'text-p-2')}>
          {t('capsuleCreate.coauthors.loading')}
        </span>
      );
    }
    if (!hasCustodians) {
      return (
        <span className={cn(styles.custodians_text, 'text-p-2')}>
          {t('capsuleCreation.activation.noCustodians')}
        </span>
      );
    }
    const removeTitle = t('capsuleCreation.activation.remove');
    const reinviteTitle = t('capsuleCreate.coauthors.reinvite');
    if (capsuleId) {
      return (
        <div className={styles.custodians_list}>
          {coauthorInvites.map(invite => (
            <M_InviteItem
              key={invite.id}
              invite={invite}
              onReinvite={handleReinvite}
              onRemove={handleRemove}
              reinviteTitle={reinviteTitle}
              removeTitle={removeTitle}
            />
          ))}
          {acceptedCustodians.map(invite => (
            <M_InviteItem
              key={invite.id}
              invite={invite}
              onRemove={handleRemove}
              removeTitle={removeTitle}
            />
          ))}
        </div>
      );
    }
    return (
      <div className={styles.custodians_list}>
        {createModeCustodiansList.map(userInfo => (
          <M_InviteItem
            key={userInfo.id}
            invite={inviteFromUserInfo(userInfo)}
            onRemove={handleRemove}
            removeTitle={removeTitle}
          />
        ))}
      </div>
    );
  };

  const handleActivationTypeChange = (type: ActivationType) => {
    onActivationTypeChange?.(type);
  };

  const parsedDate = useMemo(
    () => parseScheduledDate(scheduledDate),
    [scheduledDate]
  );

  const handleDateButtonClick = () => {
    if (activationType !== 'scheduled') return;
    setIsPopupOpen(true);
  };

  const handleInviteCustodianClick = () => {
    if (activationType !== 'custodian') return;
    setIsInviteCustodianPopupOpen(true);
  };

  const handleDateSelect = (date: Date) => {
    onDateChange?.(date.toISOString());
    setIsPopupOpen(false);
  };

  const formattedDate = useMemo(() => {
    if (activationType !== 'scheduled' || !parsedDate) {
      return undefined;
    }
    return formatActivationDate(parsedDate, language);
  }, [activationType, parsedDate, language]);

  const selectedDateText = formattedDate
    ? t('capsuleCreation.activation.selectedDate').replace(
        '{{date}}',
        formattedDate
      )
    : undefined;

  const showDateButton = activationType === 'scheduled';
  const showInviteCustodianButton = activationType === 'custodian';

  return (
    <div className={styles.wrapper}>
      <h4 className={styles.title}>{t('capsuleCreation.activation.title')}</h4>
      <div className={styles.radio_list}>
        <A_RadioItem
          label={t('capsuleCreation.activation.manual')}
          checked={activationType === 'manual'}
          onChange={() => handleActivationTypeChange('manual')}
          value='manual'
          title='Вы сможете открыть капсулу вручную в любой момент'
        />
        <A_RadioItem
          label={t('capsuleCreation.activation.scheduled')}
          checked={activationType === 'scheduled'}
          onChange={() => handleActivationTypeChange('scheduled')}
          value='scheduled'
          title='Капсула откроется автоматически в указанную дату и время'
        />
        <A_RadioItem
          label={t('capsuleCreation.activation.custodian')}
          checked={activationType === 'custodian'}
          onChange={() => handleActivationTypeChange('custodian')}
          value='custodian'
          title='Капсула жизни — будет опубликована доверенным хранителем после вашей смерти'
        />
      </div>
      {selectedDateText && (
        <div className={styles.selected_date}>
          <span className='text-button'>{selectedDateText}</span>
        </div>
      )}
      {showInviteCustodianButton && (
        <div className={styles.custodians_block}>
          {renderCustodiansBlockContent()}
        </div>
      )}
      {showDateButton && (
        <A_Button
          onClick={handleDateButtonClick}
          variant='primary'
          size='large'
          fontSize='text-button'
        >
          {t('capsuleCreation.activation.selectDate')}
        </A_Button>
      )}
      {showInviteCustodianButton && (
        <A_Button
          onClick={handleInviteCustodianClick}
          variant='tertiary'
          size='small'
          fontSize='text-p-4'
        >
          {t('capsuleCreation.activation.inviteCustodian')}
        </A_Button>
      )}
      {isPopupOpen && (
        <S_ActivationCapsulePopup
          setIsActivationCapsulePopupOpen={setIsPopupOpen}
          onSelectDate={handleDateSelect}
          initialDate={parsedDate}
        />
      )}
      {isInviteCustodianPopupOpen && (
        <S_InviteCoauthorsPopup
          setIsInviteCoauthorsPopupOpen={setIsInviteCustodianPopupOpen}
          capsuleId={capsuleId}
          mode='coauthor'
          onUserSelect={onCustodianSelect}
          onEmailInvite={onCustodianEmailInvite}
        />
      )}
    </div>
  );
}
